import { Dialog, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon, SunIcon, MoonIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";

interface BurgerMenuProps {
  links: { label: string; to: string }[];
  onLogout?: () => void;
}

export function BurgerMenu({ links, onLogout }: BurgerMenuProps) {
  const [open, setOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* Toggle button — visible only on mobile (caller wraps in md:hidden) */}
      <button
        type="button"
        aria-label="Open navigation menu"
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center p-2 rounded text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 min-h-[44px] min-w-[44px]"
      >
        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
      </button>

      {/* Full-screen overlay — Headless UI Dialog handles overflow:hidden + Escape */}
      <Transition show={open} as={Fragment}>
        <Dialog open={open} onClose={() => setOpen(false)} className="fixed inset-0 z-40">
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="transition-opacity duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-black/40 z-40"
              aria-hidden="true"
            />
          </Transition.Child>

          {/* Panel */}
          <Transition.Child
            as={Fragment}
            enter="transition-transform duration-300 ease-out"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition-transform duration-300 ease-in"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="fixed inset-0 z-50 bg-white dark:bg-slate-900 flex flex-col px-6 py-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <span className="font-bold text-lg text-slate-900 dark:text-slate-100">
                  Jobs Bot
                </span>
                <button
                  type="button"
                  aria-label="Close navigation menu"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center justify-center p-2 rounded text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 min-h-[44px] min-w-[44px]"
                >
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              {/* Nav links */}
              <nav className="flex flex-col gap-2">
                {links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className="px-4 py-3 rounded text-base font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 no-underline min-h-[44px] flex items-center"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>

              {/* Theme toggle */}
              <div className="mt-auto pt-6 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={toggleTheme}
                  aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                  className="w-full px-4 py-3 rounded text-base font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 min-h-[44px] flex items-center gap-3"
                >
                  {theme === "dark" ? (
                    <SunIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <MoonIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                  {theme === "dark" ? "Light mode" : "Dark mode"}
                </button>
                {onLogout && (
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onLogout();
                    }}
                    className="w-full mt-2 px-4 py-3 rounded text-base font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 min-h-[44px] flex items-center"
                  >
                    Log out
                  </button>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  );
}
